import { useEffect, useRef, useState, useMemo } from 'react';

import WorkerModule from '../workers/viewPreview.worker.ts?worker';

export type UseWorkerOptions = {
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  enabled?: boolean;
};

export const useWorker = ({ canvasRef, enabled = true }: UseWorkerOptions) => {
  const workerRef = useRef<Worker | null>(null);
  const offscreenRef = useRef<OffscreenCanvas | null>(null);
  const [ready, setReady] = useState(false);
  const [done, setDone] = useState(false);
  const workerId = useRef<string | null>(null);

  if (!workerId.current) {
    workerId.current = Math.round(Math.random() * 1000000).toString();
  }

  // Create worker once
  const worker = useMemo(() => {
    const w = new WorkerModule();

    return w;
  }, []);

  useEffect(() => {
    if (!worker) return;

    const handleMessage = (event: MessageEvent) => {
      const data = event.data;

      if (data.ready) {
        setReady(true);
      }

      if (data.done) {
        setDone(true);
      }
    };

    worker.addEventListener('message', handleMessage);

    return () => {
      worker.removeEventListener('message', handleMessage);
    };
  }, [worker]);

  useEffect(() => {
    if (!enabled || !canvasRef.current || !worker || offscreenRef.current)
      return;

    try {
      const offscreen = canvasRef.current.transferControlToOffscreen();
      offscreenRef.current = offscreen;
      worker.postMessage(
        { type: 'init', canvas: offscreen, workerId: workerId.current },
        [offscreen]
      );
    } catch {
      return;
    }

    return () => {
      // do not reset offscreenRef here — it’s permanently transferred
    };
  }, [canvasRef, worker, enabled]);

  const postMessage = (msg: object, transfer: Transferable[] = []) => {
    if (worker) {
      worker.postMessage({ ...msg, workerId: workerId.current }, transfer);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
    };
  }, []);

  return { worker, ready, done, postMessage };
};
